// src/components/vitals/VitalsTrendChart.tsx
// Reusable vitals trend chart (KMC #34) — IP Case Sheet (in-stay trend) and
// Patient Records (long-term history). Built on recharts, the chart library
// this project already declares (docs/HMS_V4_DESIGN_DOCUMENT.md) and the one
// the Reports/MIS pack should reuse — not a second library.
//
// ── What it plots, and where ─────────────────────────────────────
// Every point sits at its TIME TAKEN (vitals.recorded_at) on a real time
// axis — not evenly spaced by index. A reading entered late (KMC #36) is
// therefore drawn where it was measured, and marked with a hollow point; its
// entry time and reason are in the tooltip and the table view. Only the
// current version of an amended reading is ever passed in (the APIs filter
// superseded rows), and an amended point says so in its tooltip.
//
// ── Reads one source ─────────────────────────────────────────────
// Callers hand in rows from the existing vitals APIs as-is. There is no
// vitals store here, and nothing is derived that the row does not carry,
// except unit normalization of temperature (see lib/vitalReferenceBands.ts).
//
// ── Design ───────────────────────────────────────────────────────
// One metric at a time behind a chip row, one y-axis, never two. BP is the
// only two-series metric (systolic slot 1, diastolic slot 2 — validated
// palette, theme.css --color-viz-*), so it alone gets a legend. Reference
// lines use the reserved status inks, dashed, each with a text label, so no
// threshold is conveyed by colour alone. A table view carries every number.
import { useMemo, useState } from 'react';
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { cn } from '@/lib/cn';
import {
  normalizeTemp, type ReferenceLineSpec, type TempUnit, type TrendMetric,
} from '@/lib/vitalReferenceBands';

/** The subset of a vitals row this chart reads — satisfied by both the IP
 * Case Sheet's Vitals and Patient Records' vitals rows. */
export interface TrendReading {
  id?: number;
  recorded_at?: string | null;
  entered_at?: string | null;
  late_entry?: boolean | null;
  late_entry_reason?: string | null;
  amendment_reason?: string | null;
  version?: number | null;
  admission_id?: number | null;
  bp_systolic?: number | string | null;
  bp_diastolic?: number | string | null;
  pulse_rate?: number | string | null;
  temperature?: number | string | null;
  spo2?: number | string | null;
  weight_kg?: number | string | null;
  bmi?: number | string | null;
}

interface MetricDef {
  id: TrendMetric;
  label: string;
  unit: (t: TempUnit) => string;
  series: { key: 'v1' | 'v2'; name: string; color: string }[];
  pick: (r: TrendReading, t: TempUnit) => [number | null, number | null];
  /** Smallest y-padding, in the metric's own unit, so a flat series is still readable. */
  minPad: number;
}

const num = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const S1 = 'var(--color-viz-1)';
const S2 = 'var(--color-viz-2)';

export const METRICS: MetricDef[] = [
  { id: 'bp', label: 'Blood pressure', unit: () => 'mmHg', minPad: 5,
    series: [{ key: 'v1', name: 'Systolic', color: S1 }, { key: 'v2', name: 'Diastolic', color: S2 }],
    pick: r => [num(r.bp_systolic), num(r.bp_diastolic)] },
  { id: 'pulse', label: 'Pulse', unit: () => 'bpm', minPad: 5,
    series: [{ key: 'v1', name: 'Pulse', color: S1 }], pick: r => [num(r.pulse_rate), null] },
  { id: 'temp', label: 'Temperature', unit: t => `°${t}`, minPad: 0.5,
    series: [{ key: 'v1', name: 'Temperature', color: S1 }],
    pick: (r, t) => { const v = num(r.temperature); return [v == null ? null : normalizeTemp(v, t), null]; } },
  { id: 'spo2', label: 'SpO₂', unit: () => '%', minPad: 2,
    series: [{ key: 'v1', name: 'SpO₂', color: S1 }], pick: r => [num(r.spo2), null] },
  { id: 'weight', label: 'Weight', unit: () => 'kg', minPad: 1,
    series: [{ key: 'v1', name: 'Weight', color: S1 }], pick: r => [num(r.weight_kg), null] },
  { id: 'bmi', label: 'BMI', unit: () => 'kg/m²', minPad: 0.5,
    series: [{ key: 'v1', name: 'BMI', color: S1 }], pick: r => [num(r.bmi), null] },
];

// ── Axis ticks ───────────────────────────────────────────────────
// Computed here rather than left to recharts: its auto ticks on a time scale
// land on arbitrary instants, which printed "27 Sep 27 Sep" across a stay and
// y labels like 141/121/101. Ticks are aligned to real boundaries (hours,
// midnights, month starts) and to 1/2/5 × 10ⁿ steps.

const HOUR = 3600000, DAY = 24 * HOUR;
const TIME_STEPS = [HOUR, 2 * HOUR, 3 * HOUR, 6 * HOUR, 12 * HOUR, DAY, 2 * DAY, 7 * DAY, 14 * DAY];

function timeTicks(lo: number, hi: number, maxTicks = 7): { ticks: number[]; label: (t: number) => string } {
  const span = hi - lo;
  const step = TIME_STEPS.find(s => span / s <= maxTicks);
  if (step) {
    // Align to the step in LOCAL time (a midnight tick must be local midnight).
    const start = new Date(lo);
    start.setMinutes(0, 0, 0);
    if (step >= DAY) start.setHours(0);
    else start.setHours(Math.ceil(start.getHours() / (step / HOUR)) * (step / HOUR));
    const ticks: number[] = [];
    for (let t = start.getTime(); t <= hi; t += step) if (t >= lo) ticks.push(t);
    const label = (t: number) => {
      const d = new Date(t);
      if (step >= DAY || (d.getHours() === 0 && d.getMinutes() === 0)) return fmtDay(d);
      return fmtTime(d);
    };
    return { ticks, label };
  }
  // Months: first of each month, thinned to fit.
  const months: number[] = [];
  const m = new Date(lo); m.setDate(1); m.setHours(0, 0, 0, 0);
  for (; m.getTime() <= hi; m.setMonth(m.getMonth() + 1)) if (m.getTime() >= lo) months.push(m.getTime());
  const every = Math.max(1, Math.ceil(months.length / maxTicks));
  return {
    ticks: months.filter((_, i) => i % every === 0),
    label: t => { const d = new Date(t); return `${MONTHS[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`; },
  };
}

function niceTicks(lo: number, hi: number, target = 5): { ticks: number[]; min: number; max: number } {
  const raw = (hi - lo) / target || 1;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
  const min = Math.floor(lo / step) * step;
  const max = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let v = min; v <= max + step / 2; v += step) ticks.push(Number(v.toFixed(6)));
  return { ticks, min, max };
}

interface Point {
  t: number;
  v1: number | null;
  v2: number | null;
  row: TrendReading;
}

// Fixed month names rather than Intl's short month — newer ICU prints "Sept".
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n: number) => String(n).padStart(2, '0');
const fmtDay = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
const fmtTime = (d: Date) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
export const fmtStamp = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : `${fmtDay(d)} ${d.getFullYear()} ${fmtTime(d)}`;
};

/** Same test the Case Sheet table uses for its "Late entry" note. */
export function isLate(r: TrendReading): boolean {
  return !!r.late_entry;
}

function ChartTooltip({ active, payload, metric, unit }: {
  active?: boolean;
  payload?: { payload: Point }[];
  metric: MetricDef;
  unit: string;
}) {
  const p = active && payload?.length ? payload[0]!.payload : null;
  if (!p) return null;
  const r = p.row;
  const values = metric.series
    .map(s => ({ s, v: p[s.key] }))
    .filter(x => x.v != null);
  return (
    <div className="max-w-[260px] rounded-md border border-border bg-surface px-3 py-2 text-[11.5px] shadow-md">
      <div className="font-semibold text-ink">Taken {fmtStamp(r.recorded_at)}</div>
      {values.map(({ s, v }) => (
        <div key={s.key} className="mt-0.5 flex items-center gap-1.5 tabular-nums text-ink">
          {metric.series.length > 1 && (
            <svg width="10" height="10" aria-hidden><circle cx="5" cy="5" r="4" fill={s.color} /></svg>
          )}
          {metric.series.length > 1 ? `${s.name} ` : ''}{v} {unit}
        </div>
      ))}
      {r.entered_at && (
        <div className="mt-1 text-ink-muted">
          Entered {fmtStamp(r.entered_at)}
          {isLate(r) && <span className="font-semibold text-warn-ink"> · Late entry</span>}
        </div>
      )}
      {isLate(r) && r.late_entry_reason && <div className="text-ink-muted">Reason: {r.late_entry_reason}</div>}
      {(r.version ?? 1) > 1 && (
        <div className="mt-1 text-ink-muted">
          Amended (v{r.version}){r.amendment_reason ? `: ${r.amendment_reason}` : ''}
        </div>
      )}
      {r.admission_id == null && r.admission_id !== undefined && <div className="text-ink-muted">OP visit reading</div>}
    </div>
  );
}

export interface VitalsTrendChartProps {
  readings: TrendReading[];
  tempUnit: TempUnit;
  /** Reference lines for a metric, already filtered for the patient's age by
   * the caller (see lib/vitalReferenceBands.ts). Return [] for none. */
  referenceLines?: (metric: TrendMetric) => ReferenceLineSpec[];
  /** One line under the chart saying where the lines come from — or, for a
   * child, why there are none. */
  referenceNote?: string;
  initialMetric?: TrendMetric;
  height?: number;
  className?: string;
}

export function VitalsTrendChart({
  readings, tempUnit, referenceLines, referenceNote, initialMetric = 'bp', height = 220, className,
}: VitalsTrendChartProps) {
  const available = useMemo(() => METRICS.filter(m =>
    readings.some(r => { const [a, b] = m.pick(r, tempUnit); return a != null || b != null; })), [readings, tempUnit]);
  const [picked, setPicked] = useState<TrendMetric>(initialMetric);
  const [showTable, setShowTable] = useState(false);
  const metric = available.find(m => m.id === picked) ?? available[0];

  const points: Point[] = useMemo(() => {
    if (!metric) return [];
    return readings
      .filter(r => r.recorded_at && !Number.isNaN(new Date(r.recorded_at).getTime()))
      .map(r => { const [v1, v2] = metric.pick(r, tempUnit); return { t: new Date(r.recorded_at!).getTime(), v1, v2, row: r }; })
      .filter(p => p.v1 != null || p.v2 != null)
      .sort((a, b) => a.t - b.t);
  }, [readings, metric, tempUnit]);

  if (!metric) {
    return <div className={cn('py-6 text-center text-[12.5px] text-ink-muted', className)}>No vitals recorded to chart.</div>;
  }
  const unit = metric.unit(tempUnit);

  // y-domain: the data, padded, widened only to take in reference lines that
  // sit near it — a distant threshold must not flatten the actual trend.
  const values = points.flatMap(p => [p.v1, p.v2]).filter((v): v is number => v != null);
  const lo = Math.min(...values), hi = Math.max(...values);
  const pad = Math.max((hi - lo) * 0.15, metric.minPad);
  // Lines at the same value (OPD "DBP >90" and "SBP <90") become one line with
  // a joined label, so two labels never print over each other; the more
  // severe style wins.
  const refs = Object.values(
    (referenceLines?.(metric.id) ?? [])
      .filter(r => r.value >= lo - pad * 3 && r.value <= hi + pad * 3)
      .reduce<Record<string, ReferenceLineSpec>>((acc, r) => {
        const k = String(r.value);
        const prev = acc[k];
        acc[k] = prev
          ? { value: r.value, label: `${prev.label} / ${r.label}`, severity: prev.severity === 'critical' || r.severity === 'critical' ? 'critical' : 'warn' }
          : r;
        return acc;
      }, {}),
  );
  const gutter = refs.length ? Math.min(150, 14 + 5.6 * Math.max(...refs.map(r => r.label.length))) : 16;
  const y = niceTicks(
    Math.min(lo - pad, ...refs.map(r => r.value - pad / 2)),
    Math.max(hi + pad, ...refs.map(r => r.value + pad / 2)),
  );
  const decimals = metric.id === 'temp' || metric.id === 'bmi' ? 1 : 0;
  const round = (v: number) => Number(v.toFixed(decimals));

  // Time axis: a single point gets an hour either side so it is not pinned
  // to an edge.
  const tMin = points[0]?.t ?? Date.now();
  const tMax = points[points.length - 1]?.t ?? Date.now();
  const spanMs = tMax - tMin;
  const domain: [number, number] = spanMs === 0 ? [tMin - HOUR, tMax + HOUR] : [tMin - spanMs * 0.03, tMax + spanMs * 0.03];
  const x = timeTicks(domain[0], domain[1]);

  // Hollow ring for a late entry (secondary encoding, not colour alone);
  // 8px markers per the house mark spec, with a 2px surface ring so a point
  // on the line stays distinct from it.
  const dotFor = (color: string, key: 'v1' | 'v2') => (props: { cx?: number; cy?: number; payload?: Point; index?: number }) => {
    const { cx, cy, payload, index } = props;
    if (cx == null || cy == null || payload?.[key] == null) return <g key={`${key}-${index}`} />;
    const late = isLate(payload.row);
    return (
      <circle key={`${key}-${index}`} cx={cx} cy={cy} r={4}
        fill={late ? 'var(--color-surface)' : color}
        stroke={late ? color : 'var(--color-surface)'} strokeWidth={2} />
    );
  };

  const lateCount = points.filter(p => isLate(p.row)).length;
  const latest = points[points.length - 1];

  return (
    <div className={cn('min-w-0', className)}>
      {/* Metric chips — one row, above the chart. */}
      <div className="mb-2 flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Vital to chart">
        {available.map(m => (
          <button key={m.id} type="button" role="tab" aria-selected={m.id === metric.id}
            onClick={() => setPicked(m.id)}
            className={cn('rounded-full border px-2.5 py-0.5 text-[11.5px] font-semibold transition-colors',
              m.id === metric.id ? 'border-teal bg-teal-light text-teal' : 'border-border text-ink-muted hover:text-ink')}>
            {m.label}
          </button>
        ))}
        <span className="ml-auto text-[11px] tabular-nums text-ink-muted">
          {points.length} reading{points.length === 1 ? '' : 's'}
          {latest && <> · latest {metric.series.map(s => latest[s.key]).filter(v => v != null).join('/')} {unit}</>}
        </span>
      </div>

      {metric.series.length > 1 && (
        <div className="mb-1 flex items-center gap-3 text-[11px] text-ink-muted" aria-hidden>
          {metric.series.map(s => (
            <span key={s.key} className="inline-flex items-center gap-1">
              <svg width="14" height="8"><line x1="0" y1="4" x2="14" y2="4" stroke={s.color} strokeWidth="2" /></svg>
              {s.name}
            </span>
          ))}
        </div>
      )}

      <div role="img" aria-label={`${metric.label} trend, ${points.length} readings plotted by time taken`}>
        <ResponsiveContainer width="100%" height={height}>
          {/* Right margin is the gutter the threshold labels sit in — outside
              the plot, so a label can never sit on top of a reading. */}
          <LineChart data={points} margin={{ top: 8, right: gutter, bottom: 0, left: 0 }}>
            <CartesianGrid stroke="var(--color-line)" strokeWidth={1} vertical={false} />
            <XAxis dataKey="t" type="number" scale="time" domain={domain} ticks={x.ticks} tickFormatter={x.label}
              tick={{ fontSize: 10.5, fill: 'var(--color-ink-muted)' }} tickLine={false}
              axisLine={{ stroke: 'var(--color-line)' }} interval="preserveStartEnd" />
            <YAxis domain={[y.min, y.max]} ticks={y.ticks} width={40}
              tick={{ fontSize: 10.5, fill: 'var(--color-ink-muted)' }} tickLine={false} axisLine={false}
              tickFormatter={(v: number) => String(round(v))} />
            {refs.map(r => (
              <ReferenceLine key={`${r.label}-${r.value}`} y={r.value}
                stroke={r.severity === 'critical' ? 'var(--color-danger-ink)' : 'var(--color-warn-ink)'}
                strokeDasharray="4 4" strokeWidth={1} ifOverflow="discard"
                label={{ value: r.label, position: 'right', fontSize: 10, fill: 'var(--color-ink-muted)' }} />
            ))}
            <Tooltip content={<ChartTooltip metric={metric} unit={unit} />}
              cursor={{ stroke: 'var(--color-ink-muted)', strokeWidth: 1, strokeDasharray: '2 2' }}
              isAnimationActive={false} />
            {metric.series.map(s => (
              <Line key={s.key} dataKey={s.key} name={s.name} type="linear" stroke={s.color} strokeWidth={2}
                connectNulls isAnimationActive={false} dot={dotFor(s.color, s.key)}
                activeDot={{ r: 5, stroke: 'var(--color-surface)', strokeWidth: 2 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px] text-ink-muted">
        <span>
          Plotted at time taken.
          {lateCount > 0 && <> Hollow point = late entry ({lateCount}).</>}
          {referenceNote && <> {referenceNote}</>}
        </span>
        <button type="button" onClick={() => setShowTable(v => !v)}
          className="font-semibold text-teal underline-offset-2 hover:underline">
          {showTable ? 'Hide table' : 'Show table'}
        </button>
      </div>

      {showTable && (
        <div className="-mx-1 mt-2 max-h-64 overflow-auto px-1">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-ink-muted">
                <th className="py-1 pr-3 font-semibold">Taken</th>
                <th className="py-1 pr-3 font-semibold">{metric.label} ({unit})</th>
                <th className="py-1 pr-3 font-semibold">Entered</th>
                <th className="py-1 font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {[...points].reverse().map((p, i) => (
                <tr key={p.row.id ?? i} className="border-b border-border/60 last:border-0">
                  <td className="whitespace-nowrap py-1 pr-3 tabular-nums">{fmtStamp(p.row.recorded_at)}</td>
                  <td className="py-1 pr-3 tabular-nums">{metric.series.map(s => p[s.key] ?? '—').join(' / ')}</td>
                  <td className="whitespace-nowrap py-1 pr-3 tabular-nums text-ink-muted">{fmtStamp(p.row.entered_at)}</td>
                  <td className="py-1 text-ink-muted">
                    {[isLate(p.row) ? `Late entry${p.row.late_entry_reason ? ` — ${p.row.late_entry_reason}` : ''}` : null,
                      (p.row.version ?? 1) > 1 ? `Amended v${p.row.version}` : null].filter(Boolean).join(' · ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default VitalsTrendChart;
